import { supabaseDataProvider } from "ra-supabase-core";
import {
  withLifecycleCallbacks,
  type DataProvider,
  type GetListParams,
  type Identifier,
  type ResourceCallbacks,
} from "ra-core";
import type {
  CampaignProgressSummary,
  CampaignTaskGenerationResult,
  ContactNote,
  Deal,
  DealNote,
  RAFile,
  Sale,
  SalesFormData,
  SignUpData,
  Task,
} from "../../types";
import type { ConfigurationContextValue } from "../../root/ConfigurationContext";
import { ATTACHMENTS_BUCKET } from "../commons/attachments";
import { getIsInitialized } from "./authProvider";
import { getSupabaseClient } from "./supabase";
import { normalizeRecurringTaskPayload } from "../../tasks/taskRecurrence";
import {
  applySegmentMembershipFilter,
} from "../commons/applySegmentMembershipFilter";
import {
  fetchCompanyIdsByBcSegment,
  fetchContactIdsByBcSegment,
} from "../commons/bcSegmentLookup";
import { applyFullTextSearch } from "./fullTextSearch";

const getBaseDataProvider = () =>
  supabaseDataProvider({
    instanceUrl: import.meta.env.VITE_SUPABASE_URL,
    apiKey: import.meta.env.VITE_SB_PUBLISHABLE_KEY,
    supabaseClient: getSupabaseClient(),
    sortOrder: "asc,desc.nullslast" as any,
  });

const processCompanyLogo = async (params: any) => {
  const logo = params.data.logo;

  if (logo?.rawFile instanceof File) {
    await uploadToBucket(logo);
  }

  return {
    ...params,
    data: {
      ...params.data,
      logo,
    },
  };
};

const getDataProviderWithCustomMethods = () => {
  const baseDataProvider = getBaseDataProvider();
  const getListWithFallback = async (
    preferredResource: string,
    fallbackResource: string,
    params: GetListParams,
  ) => {
    try {
      return await baseDataProvider.getList(preferredResource, params);
    } catch {
      // Allow live environments where summary views haven't been deployed yet.
      return baseDataProvider.getList(fallbackResource, params);
    }
  };

  const getOneWithFallback = async (
    preferredResource: string,
    fallbackResource: string,
    params: any,
  ) => {
    try {
      return await baseDataProvider.getOne(preferredResource, params);
    } catch {
      // Allow live environments where summary views haven't been deployed yet.
      return baseDataProvider.getOne(fallbackResource, params);
    }
  };

  return {
    ...baseDataProvider,
    async getList(resource: string, params: GetListParams) {
      if (resource === "companies") {
        return baseDataProvider.getList("companies_summary", params);
      }
      if (resource === "contacts") {
        return baseDataProvider.getList("contacts_summary", params);
      }
      if (resource === "campaigns") {
        return getListWithFallback("campaigns_summary", "campaigns", params);
      }
      if (resource === "campaign_tasks_progress") {
        try {
          const result = await baseDataProvider.getList(
            "campaign_tasks_progress",
            params,
          );
          return {
            ...result,
            data: result.data.map((row: any) => ({
              ...row,
              id: row.task_id,
            })),
          };
        } catch {
          return { data: [], total: 0 };
        }
      }
      if (resource === "activity_log") {
        const { data, total } = await baseDataProvider.getList(
          "activity_log",
          params,
        );
        // Rename snake_case view columns to camelCase to match Activity type
        return {
          data: data.map((row: any) => ({
            ...row,
            contactNote: row.contact_note ?? undefined,
            dealNote: row.deal_note ?? undefined,
            companyNote: row.company_note ?? undefined,
            contact_note: undefined,
            deal_note: undefined,
            company_note: undefined,
          })),
          total,
        };
      }

      return baseDataProvider.getList(resource, params);
    },
    async create(resource: string, params: any) {
      if (resource === "tasks") {
        const normalized = normalizeRecurringTaskPayload(params.data);
        if (normalized.recurrence.enabled) {
          const { data, error } = await getSupabaseClient().rpc(
            "create_task_with_recurrence",
            {
              p_contact_id: normalized.baseTask.contact_id,
              p_type: normalized.baseTask.type,
              p_text: normalized.baseTask.text,
              p_due_date: normalized.baseTask.due_date,
              p_sales_id: normalized.baseTask.sales_id ?? null,
              p_recurrence_enabled: true,
              p_interval_type: normalized.recurrence.intervalType,
              p_interval_count: normalized.recurrence.intervalCount,
              p_interval_unit: normalized.recurrence.intervalUnit,
              p_ends_at: normalized.recurrence.endsAt,
            },
          );
          if (error) {
            throw error;
          }
          return { data: data as any };
        }

        return baseDataProvider.create("tasks", {
          ...params,
          data: normalized.baseTask,
        });
      }

      if (
        resource === "campaign_task_definitions" ||
        resource === "campaign_task_targets" ||
        resource === "campaign_targets"
      ) {
        const { data, error } = await getSupabaseClient()
          .from(resource)
          .insert(params.data)
          .select()
          .single();
        if (error) {
          throw error;
        }
        return { data };
      }

      return baseDataProvider.create(resource, params);
    },
    async getOne(resource: string, params: any) {
      if (resource === "companies") {
        return baseDataProvider.getOne("companies_summary", params);
      }
      if (resource === "contacts") {
        return baseDataProvider.getOne("contacts_summary", params);
      }
      if (resource === "campaigns") {
        return getOneWithFallback("campaigns_summary", "campaigns", params);
      }

      return baseDataProvider.getOne(resource, params);
    },

    async signUp({ email, password, first_name, last_name }: SignUpData) {
      const response = await getSupabaseClient().auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name,
            last_name,
          },
        },
      });

      if (!response.data?.user || response.error) {
        console.error("signUp.error", response.error);
        throw new Error(response?.error?.message || "Failed to create account");
      }

      // Update the is initialized cache
      (getIsInitialized as any)._is_initialized_cache = true;

      return {
        id: response.data.user.id,
        email,
        password,
      };
    },
    async salesCreate(body: SalesFormData) {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        data: Sale;
      }>("users", {
        method: "POST",
        body,
      });

      if (!data || error) {
        console.error("salesCreate.error", error);
        const errorDetails = await (async () => {
          try {
            return (await error?.context?.json()) ?? {};
          } catch {
            return {};
          }
        })();
        throw new Error(errorDetails?.message || "Failed to create the user");
      }

      return data.data;
    },
    async salesUpdate(
      id: Identifier,
      data: Partial<Omit<SalesFormData, "password">>,
    ) {
      const { email, first_name, last_name, administrator, manager, avatar, disabled } =
        data;

      const { data: updatedData, error } =
        await getSupabaseClient().functions.invoke<{
          data: Sale;
        }>("users", {
          method: "PATCH",
          body: {
            sales_id: id,
            email,
            first_name,
            last_name,
            administrator,
            manager,
            disabled,
            avatar,
          },
        });

      if (!updatedData || error) {
        console.error("salesCreate.error", error);
        throw new Error("Failed to update account manager");
      }

      return updatedData.data;
    },
    async updatePassword(id: Identifier) {
      const { data: passwordUpdated, error } =
        await getSupabaseClient().functions.invoke<boolean>("update_password", {
          method: "PATCH",
          body: {
            sales_id: id,
          },
        });

      if (!passwordUpdated || error) {
        console.error("update_password.error", error);
        throw new Error("Failed to update password");
      }

      return passwordUpdated;
    },
    async unarchiveDeal(deal: Deal) {
      // get all deals where stage is the same as the deal to unarchive
      const { data: deals } = await baseDataProvider.getList<Deal>("deals", {
        filter: { stage: deal.stage },
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "index", order: "ASC" },
      });

      // set index for each deal starting from 1, if the deal to unarchive is found, set its index to the last one
      const updatedDeals = deals.map((d, index) => ({
        ...d,
        index: d.id === deal.id ? 0 : index + 1,
        archived_at: d.id === deal.id ? null : d.archived_at,
      }));

      return await Promise.all(
        updatedDeals.map((updatedDeal) =>
          baseDataProvider.update("deals", {
            id: updatedDeal.id,
            data: updatedDeal,
            previousData: deals.find((d) => d.id === updatedDeal.id),
          }),
        ),
      );
    },
    async isInitialized() {
      return getIsInitialized();
    },
    async mergeContacts(sourceId: Identifier, targetId: Identifier) {
      const { data, error } = await getSupabaseClient().functions.invoke(
        "merge_contacts",
        {
          method: "POST",
          body: { loserId: sourceId, winnerId: targetId },
        },
      );

      if (error) {
        console.error("merge_contacts.error", error);
        throw new Error("Failed to merge contacts");
      }

      return data;
    },
    async createTaskWithRecurrence(data: any) {
      const normalized = normalizeRecurringTaskPayload(data);
      if (!normalized.recurrence.enabled) {
        return baseDataProvider.create("tasks", { data: normalized.baseTask });
      }
      const { data: created, error } = await getSupabaseClient().rpc(
        "create_task_with_recurrence",
        {
          p_contact_id: normalized.baseTask.contact_id,
          p_type: normalized.baseTask.type,
          p_text: normalized.baseTask.text,
          p_due_date: normalized.baseTask.due_date,
          p_sales_id: normalized.baseTask.sales_id ?? null,
          p_recurrence_enabled: true,
          p_interval_type: normalized.recurrence.intervalType,
          p_interval_count: normalized.recurrence.intervalCount,
          p_interval_unit: normalized.recurrence.intervalUnit,
          p_ends_at: normalized.recurrence.endsAt,
        },
      );
      if (error) throw error;
      return { data: created as Task };
    },
    async getConfiguration(): Promise<ConfigurationContextValue> {
      const { data } = await baseDataProvider.getOne("configuration", {
        id: 1,
      });
      return (data?.config as ConfigurationContextValue) ?? {};
    },
    async generateCampaignTasks(
      definitionId: Identifier,
      fallbackSalesId?: Identifier | null,
    ): Promise<CampaignTaskGenerationResult> {
      const { data, error } = await getSupabaseClient().rpc(
        "generate_campaign_tasks",
        {
          p_definition_id: definitionId,
          p_fallback_sales_id: fallbackSalesId ?? null,
        },
      );
      if (error) throw error;
      return data as CampaignTaskGenerationResult;
    },
    async reconcileCampaignTasks(
      definitionId: Identifier,
      fallbackSalesId?: Identifier | null,
    ): Promise<CampaignTaskGenerationResult> {
      const { data, error } = await getSupabaseClient().rpc(
        "reconcile_campaign_tasks",
        {
          p_definition_id: definitionId,
          p_fallback_sales_id: fallbackSalesId ?? null,
        },
      );
      if (error) throw error;
      return data as CampaignTaskGenerationResult;
    },
    async getCampaignProgress(
      campaignId: Identifier,
    ): Promise<CampaignProgressSummary> {
      const { data, error } = await getSupabaseClient().rpc(
        "get_campaign_progress",
        { p_campaign_id: campaignId },
      );
      if (error) throw error;
      return data as CampaignProgressSummary;
    },
    async updateConfiguration(
      config: ConfigurationContextValue,
    ): Promise<ConfigurationContextValue> {
      const { data } = await baseDataProvider.update("configuration", {
        id: 1,
        data: { config },
        previousData: { id: 1 },
      });
      return data.config as ConfigurationContextValue;
    },
  } satisfies DataProvider;
};

export type CrmDataProvider = ReturnType<
  typeof getDataProviderWithCustomMethods
>;

const processConfigLogo = async (logo: any): Promise<string> => {
  if (typeof logo === "string") return logo;
  if (logo?.rawFile instanceof File) {
    await uploadToBucket(logo);
    return logo.src;
  }
  return logo?.src ?? "";
};

const lifeCycleCallbacks: ResourceCallbacks[] = [
  {
    resource: "configuration",
    beforeUpdate: async (params) => {
      const config = params.data.config;
      if (config) {
        config.lightModeLogo = await processConfigLogo(config.lightModeLogo);
        config.darkModeLogo = await processConfigLogo(config.darkModeLogo);
      }
      return params;
    },
  },
  {
    resource: "contact_notes",
    beforeSave: async (data: ContactNote, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi) => uploadToBucket(fi)),
        );
      }
      return data;
    },
  },
  {
    resource: "deal_notes",
    beforeSave: async (data: DealNote, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi) => uploadToBucket(fi)),
        );
      }
      return data;
    },
  },
  {
    resource: "sales",
    beforeSave: async (data: Sale, _, __) => {
      if (data.avatar) {
        await uploadToBucket(data.avatar);
      }
      return data;
    },
  },
  {
    resource: "contacts",
    beforeGetList: async (params) => {
      const withSector = await applySegmentMembershipFilter(params, (segmentNumber) =>
        fetchContactIdsByBcSegment(getSupabaseClient(), segmentNumber),
      );
      return applyFullTextSearch([
        "first_name",
        "last_name",
        "company_name",
        "title",
        "email",
        "phone",
        "background",
      ])(withSector);
    },
  },
  {
    resource: "companies",
    beforeGetList: async (params) => {
      const withSector = await applySegmentMembershipFilter(params, (segmentNumber) =>
        fetchCompanyIdsByBcSegment(getSupabaseClient(), segmentNumber),
      );
      return applyFullTextSearch([
        "name",
        "phone_number",
        "website",
        "zipcode",
        "city",
        "state_abbr",
      ])(withSector);
    },
    beforeCreate: async (params) => {
      const createParams = await processCompanyLogo(params);

      return {
        ...createParams,
        data: {
          created_at: new Date().toISOString(),
          ...createParams.data,
        },
      };
    },
    beforeUpdate: async (params) => {
      return await processCompanyLogo(params);
    },
  },
  {
    resource: "contacts_summary",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["first_name", "last_name"])(params);
    },
  },
  {
    resource: "campaigns",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["name", "description", "product_name"])(params);
    },
  },
  {
    resource: "deals",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["name", "category", "description"])(params);
    },
  },
];

export const getDataProvider = () => {
  if (import.meta.env.VITE_SUPABASE_URL === undefined) {
    throw new Error("Please set the VITE_SUPABASE_URL environment variable");
  }
  if (import.meta.env.VITE_SB_PUBLISHABLE_KEY === undefined) {
    throw new Error(
      "Please set the VITE_SB_PUBLISHABLE_KEY environment variable",
    );
  }
  return withLifecycleCallbacks(
    getDataProviderWithCustomMethods(),
    lifeCycleCallbacks,
  ) as CrmDataProvider;
};

const uploadToBucket = async (fi: RAFile) => {
  if (!fi.src.startsWith("blob:") && !fi.src.startsWith("data:")) {
    // Sign URL check if path exists in the bucket
    if (fi.path) {
      const { error } = await getSupabaseClient()
        .storage.from(ATTACHMENTS_BUCKET)
        .createSignedUrl(fi.path, 60);

      if (!error) {
        return fi;
      }
    }
  }

  const dataContent = fi.src
    ? await fetch(fi.src)
        .then((res) => {
          if (res.status !== 200) {
            return null;
          }
          return res.blob();
        })
        .catch(() => null)
    : fi.rawFile;

  if (dataContent == null) {
    // We weren't able to download the file from its src (e.g. user must be signed in on another website to access it)
    // or the file has no content (not probable)
    // In that case, just return it as is: when trying to download it, users should be redirected to the other website
    // and see they need to be signed in. It will then be their responsibility to upload the file back to the note.
    return fi;
  }

  const file = fi.rawFile;
  const fileParts = file.name.split(".");
  const fileExt = fileParts.length > 1 ? `.${file.name.split(".").pop()}` : "";
  const fileName = `${Math.random()}${fileExt}`;
  const filePath = `${fileName}`;
  const { error: uploadError } = await getSupabaseClient()
    .storage.from(ATTACHMENTS_BUCKET)
    .upload(filePath, dataContent);

  if (uploadError) {
    console.error("uploadError", uploadError);
    throw new Error("Failed to upload attachment");
  }

  const { data } = getSupabaseClient()
    .storage.from(ATTACHMENTS_BUCKET)
    .getPublicUrl(filePath);

  fi.path = filePath;
  fi.src = data.publicUrl;

  // save MIME type
  const mimeType = file.type;
  fi.type = mimeType;

  return fi;
};
